import { useState, useEffect, useCallback } from "react";
import api from "../../lib/api";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { Select } from "../../components/ui/Select";
import { Badge } from "../../components/ui/Badge";
import { Table } from "../../components/ui/Table";
import { Modal } from "../../components/ui/Modal";
import { Pagination } from "../../components/ui/Pagination";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog";
import { ClienteSearchSelect } from "../../components/admin/ClienteSearchSelect";
import { formatFecha, formatFechaHora } from "../../lib/dates";
import { PROVINCIAS_ESPANA_OPTIONS } from "../../constants/provincias";

interface SeguroRow {
  id: number;
  cliente?: { id: number; razonSocial?: string };
  modalidad?: { id: number; nombre: string };
  eventoReferencia?: string | null;
  createdAt?: string;
  numPilotos?: number;
  importeTotal?: number;
  estado?: string;
  [key: string]: unknown;
}

interface SeguroDetail extends SeguroRow {
  cliente: { id: number; razonSocial?: string };
  modalidad: { id: number; nombre: string; tipo?: string };
  modalidades_paquete?: Array<{
    modalidadId: number;
    nombre: string;
    tipo: "diario" | "anual";
    precioVenta: number | null;
    precioBase: number;
  }>;
  pilotos: Array<{
    id: number;
    nif: string;
    nombre: string;
    apellidos: string;
    email: string;
    telefono: string;
    fechaInicio?: string;
    fechaFin?: string;
    esFacturable?: boolean;
    bajaAt?: string | null;
    certificadoUrl?: string | null;
    estado?: "pendiente" | "pagado" | "cancelado" | "error_pago";
    emitido?: boolean;
    emitidoAt?: string | null;
  }>;
  fechaFin?: string;
  metodoPago?: string;
  importePrima?: number;
  gastosGestion?: number;
}

interface PilotoListRow {
  piloto: {
    id: number;
    nif: string;
    nombre: string;
    apellidos: string;
    email: string;
    telefono: string;
    fechaInicio: string;
    fechaFin: string;
    esFacturable: boolean;
    bajaAt: string | null;
    certificadoUrl: string | null;
    estado?: "pendiente" | "pagado" | "cancelado" | "error_pago";
    emitido?: boolean;
    emitidoAt?: string | null;
  };
  seguro: {
    id: number;
    eventoReferencia: string | null;
    estado: string;
    origen: string | null;
  };
  modalidad: { nombre: string; tipo: string };
  cliente: { razonSocial: string };
}

interface PilotosListResponse {
  data: PilotoListRow[];
  total: number;
  page: number;
  limit: number;
}

function vigenciaPilotosFacturables(
  pilotos: Array<{ fechaInicio?: string; fechaFin?: string; esFacturable?: boolean }> | undefined
): string {
  const pilotosFact = pilotos?.filter((p) => p.esFacturable) ?? [];
  if (pilotosFact.length === 0) return "Sin pilotos asignados";
  const fechaMin = pilotosFact.reduce(
    (min, p) => (!min || (p.fechaInicio && p.fechaInicio < min) ? p.fechaInicio ?? min : min),
    "" as string
  );
  const fechaMax = pilotosFact.reduce(
    (max, p) => (!max || (p.fechaFin && p.fechaFin > max) ? p.fechaFin ?? max : max),
    "" as string
  );
  if (!fechaMin || !fechaMax) return "Sin pilotos asignados";
  return `${formatFechaHora(fechaMin)} → ${formatFechaHora(fechaMax)}`;
}

function estadoBadgeSeguro(estado: string | undefined) {
  if (!estado) return <Badge variant="neutral">-</Badge>;
  switch (estado) {
    case "activo": return <Badge variant="success">Activo</Badge>;
    case "cancelado": return <Badge variant="danger">Cancelado</Badge>;
    default: return <Badge variant="neutral">{estado}</Badge>;
  }
}

function estadoBadgePiloto(estado: string | undefined) {
  if (!estado) return <Badge variant="neutral">-</Badge>;
  switch (estado) {
    case "pendiente": return <Badge variant="warning">Pendiente</Badge>;
    case "pagado": return <Badge variant="success">Pagado</Badge>;
    case "cancelado": return <Badge variant="danger">Cancelado</Badge>;
    default: return <Badge variant="neutral">{estado}</Badge>;
  }
}

const ESTADO_OPTIONS = [
  { value: "", label: "Todos" },
  { value: "activo", label: "Activo" },
  { value: "cancelado", label: "Cancelado" },
];

export function SegurosPage() {
  const [viewMode, setViewMode] = useState<"seguros" | "pilotos">("seguros");
  const [data, setData] = useState<SeguroRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pilotosList, setPilotosList] = useState<PilotoListRow[]>([]);
  const [pilotosLoading, setPilotosLoading] = useState(false);
  const [pilotosPage, setPilotosPage] = useState(1);
  const [pilotosTotal, setPilotosTotal] = useState(0);
  const [filtroPilotoNif, setFiltroPilotoNif] = useState("");
  const [filtroPilotoNombre, setFiltroPilotoNombre] = useState("");
  const [exportPilotosLoading, setExportPilotosLoading] = useState(false);
  const [exportPilotosError, setExportPilotosError] = useState<string | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [modalidades, setModalidades] = useState<{ id: number; nombre: string }[]>([]);
  const [filters, setFilters] = useState({
    cliente_id: "",
    estado: "",
    modalidad_id: "",
    fecha_desde: "",
    fecha_hasta: "",
    fecha_referencia: "cobertura",
  });
  const [detailSeguro, setDetailSeguro] = useState<SeguroDetail | null>(null);
  const [detailTab, setDetailTab] = useState<"resumen" | "pilotos" | "recibo">("resumen");
  const [reciboData, setReciboData] = useState<{ recibo: unknown; pdf_url: string } | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editForm, setEditForm] = useState({ evento_referencia: "" });
  const [confirmAvisar, setConfirmAvisar] = useState<number | null>(null);
  const [confirmBaja, setConfirmBaja] = useState<{ seguroId: number; pilotoId: number } | null>(null);
  const [addPilotoOpen, setAddPilotoOpen] = useState(false);
  const [pilotoForm, setPilotoForm] = useState({
    nif: "",
    nombre: "",
    apellidos: "",
    fecha_nacimiento: "",
    email: "",
    telefono: "",
    direccion: "",
    poblacion: "",
    provincia: "",
    codigo_postal: "",
  });
  const [fechaCobertura, setFechaCobertura] = useState("");
  const [horaCobertura, setHoraCobertura] = useState("09");
  const [minutosCobertura, setMinutosCobertura] = useState("00");
  const [pilotoFormError, setPilotoFormError] = useState("");
  const [selectedModalidadId, setSelectedModalidadId] = useState<number | "">("");
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [confirmReactivar, setConfirmReactivar] = useState<{
  seguroId: number;
  pilotoId: number;
} | null>(null);

const [actionMessage, setActionMessage] = useState<{
  type: "success" | "error";
  text: string;
} | null>(null);

useEffect(() => {
  if (!actionMessage) return;

  const timer = window.setTimeout(() => {
    setActionMessage(null);
  }, 5000);

  return () => window.clearTimeout(timer);
}, [actionMessage]);

  const limit = 20;
  const pilotosLimit = 50;

  const buildSharedFilterParams = useCallback(
    (extra?: { page?: number; limit?: number; includePilotoFilters?: boolean }) => {
      const params: Record<string, string | number> = {};
      if (filters.cliente_id) params.cliente_id = filters.cliente_id;
      if (filters.estado) params.estado = filters.estado;
      if (filters.modalidad_id) params.modalidad_id = filters.modalidad_id;
      if (filters.fecha_desde) params.fecha_desde = filters.fecha_desde;
      if (filters.fecha_hasta) params.fecha_hasta = filters.fecha_hasta;
      params.fecha_referencia = filters.fecha_referencia;
      if (extra?.includePilotoFilters) {
        if (filtroPilotoNif.trim()) params.nif = filtroPilotoNif.trim();
        if (filtroPilotoNombre.trim()) params.nombre = filtroPilotoNombre.trim();
      }
      if (extra?.page != null) params.page = extra.page;
      if (extra?.limit != null) params.limit = extra.limit;
      return params;
    },
    [filters, filtroPilotoNif, filtroPilotoNombre]
  );

  const handleLimpiarFiltros = () => {
    setFilters({
      cliente_id: "",
      estado: "",
      modalidad_id: "",
      fecha_desde: "",
      fecha_hasta: "",
      fecha_referencia: "cobertura",
    });
    setFiltroPilotoNif("");
    setFiltroPilotoNombre("");
    setPage(1);
    setPilotosPage(1);
    setExportPilotosError(null);
  };

  const fetchModalidades = useCallback(async () => {
    try {
      const res = await api.get<{ id: number; nombre: string }[]>("/admin/modalidades", { params: { tipo: "todos", incluir_inactivas: "true" } });
      setModalidades(Array.isArray(res.data) ? res.data : []);
    } catch {
      setModalidades([]);
    }
  }, []);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setListError(null);
    try {
      const params: Record<string, string | number> = { page, limit };
      if (filters.cliente_id) params.cliente_id = filters.cliente_id;
      if (filters.estado) params.estado = filters.estado;
      if (filters.modalidad_id) params.modalidad_id = filters.modalidad_id;
      if (filters.fecha_desde) params.fecha_desde = filters.fecha_desde;
      if (filters.fecha_hasta) params.fecha_hasta = filters.fecha_hasta;
      params.fecha_referencia = filters.fecha_referencia;
      const res = await api.get<SeguroRow[]>("/admin/seguros", { params });
      const list = Array.isArray(res.data) ? res.data : [];
      setData(list);
      setTotalPages(list.length < limit ? page : page + 1);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status !== 401) {
        console.error("Error cargando seguros:", err);
        setListError("Error al cargar los seguros. Inténtalo de nuevo.");
      }
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => {
    fetchModalidades();
  }, [fetchModalidades]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const fetchPilotos = useCallback(async () => {
    setPilotosLoading(true);
    try {
      const params = buildSharedFilterParams({
        page: pilotosPage,
        limit: pilotosLimit,
        includePilotoFilters: true,
      });
      const res = await api.get<PilotosListResponse>("/admin/seguros/pilotos", { params });
      setPilotosList(res.data.data ?? []);
      const total = res.data.total ?? 0;
      setPilotosTotal(total);
    } catch {
      setPilotosList([]);
      setPilotosTotal(0);
    } finally {
      setPilotosLoading(false);
    }
  }, [buildSharedFilterParams, pilotosPage]);

  useEffect(() => {
    if (viewMode === "pilotos") {
      fetchPilotos();
    }
  }, [viewMode, fetchPilotos]);

  const handleExportarPilotos = async () => {
    setExportPilotosError(null);
    setExportPilotosLoading(true);
    try {
      const params = buildSharedFilterParams({ includePilotoFilters: true });
      const search = new URLSearchParams();
      Object.entries(params).forEach(([k, v]) => search.set(k, String(v)));
      const response = await api.get(`/admin/seguros/pilotos/export?${search.toString()}`, {
        responseType: "blob",
      });
      const blob = new Blob([response.data as BlobPart], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `pilotos-export-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status !== 401) {
        setExportPilotosError("Error al exportar el Excel. Inténtalo de nuevo.");
        console.error("Error exportando pilotos:", err);
      }
    } finally {
      setExportPilotosLoading(false);
    }
  };

  const openDetail = async (id: number) => {
    setModalOpen(true);
    setDetailSeguro(null);
    setReciboData(null);
    setDetailTab("resumen");
    try {
      const res = await api.get<SeguroDetail>(`/admin/seguros/${id}`);
      setDetailSeguro(res.data);
    } catch {
      setDetailSeguro(null);
    }
  };

  const loadRecibo = async (id: number) => {
    try {
      const res = await api.get<{ recibo: unknown; pdf_url: string }>(`/admin/seguros/${id}/recibo`);
      setReciboData(res.data);
    } catch {
      setReciboData(null);
    }
  };

  const handleActivarPilotosPendientes = async () => {
    if (!detailSeguro?.id) return;
    const pendientes = (detailSeguro.pilotos ?? []).filter(
      (p) => p.esFacturable && p.estado === "pendiente"
    );
    if (pendientes.length === 0) return;
    setActionLoading("estado");
    try {
      for (const p of pendientes) {
        await api.post(`/cliente/seguros/${detailSeguro.id}/pilotos/${p.id}/validar`);
      }
      const res = await api.get<SeguroDetail>(`/admin/seguros/${detailSeguro.id}`);
      setDetailSeguro(res.data);
    } finally {
      setActionLoading(null);
    }
  };

  const handleEmitirPiloto = async (pilotoId: number) => {
    if (!detailSeguro?.id) return;
    setActionLoading(`emitir-${pilotoId}`);
    try {
      await api.post(`/admin/seguros/${detailSeguro.id}/pilotos/${pilotoId}/emitir`);
      const res = await api.get<SeguroDetail>(`/admin/seguros/${detailSeguro.id}`);
      setDetailSeguro(res.data);
    } finally {
      setActionLoading(null);
    }
  };

  const handleEmitirTodos = async () => {
    if (!detailSeguro?.id) return;
    setActionLoading("emitir-todos");
    try {
      await api.post(`/admin/seguros/${detailSeguro.id}/emitir-pendientes`);
      const res = await api.get<SeguroDetail>(`/admin/seguros/${detailSeguro.id}`);
      setDetailSeguro(res.data);
    } finally {
      setActionLoading(null);
    }
  };

  const handleAvisarAseguradora = async () => {
    if (!confirmAvisar) return;
    setActionLoading("avisar");
    try {
      await api.post(`/admin/seguros/${confirmAvisar}/avisar-aseguradora`);
      setConfirmAvisar(null);
    } finally {
      setActionLoading(null);
    }
  };

  const handleSaveEdit = async () => {
    if (!detailSeguro?.id) return;
    setActionLoading("edit");
    try {
      await api.patch(`/admin/seguros/${detailSeguro.id}`, {
        evento_referencia: editForm.evento_referencia,
      });
      const res = await api.get<SeguroDetail>(`/admin/seguros/${detailSeguro.id}`);
      setDetailSeguro(res.data);
      setEditModalOpen(false);
    } finally {
      setActionLoading(null);
    }
  };

const handleBajaForzada = async () => {
  if (!confirmBaja) return;

  const target = confirmBaja;
  setActionLoading(`baja-${target.pilotoId}`);
  setActionMessage(null);

  try {
    await api.delete(
      `/admin/seguros/${target.seguroId}/pilotos/${target.pilotoId}/baja-forzada`
    );

    setActionMessage({
      type: "success",
      text: "piloto dado de baja con éxito",
    });

    if (detailSeguro?.id === target.seguroId) {
      const res = await api.get<SeguroDetail>(
        `/admin/seguros/${target.seguroId}`
      );
      setDetailSeguro(res.data);
    }

    void fetchList();
    void fetchPilotos();
  } catch (err: unknown) {
    setActionMessage({
      type: "error",
      text:
        (err as { response?: { data?: { error?: string } } })?.response?.data
          ?.error ?? "No se pudo dar de baja al piloto.",
    });
  } finally {
    setConfirmBaja(null);
    setActionLoading(null);
  }
};

const handleReactivarPiloto = async () => {
  if (!confirmReactivar) return;

  const target = confirmReactivar;
  setActionLoading(`reactivar-${target.pilotoId}`);
  setActionMessage(null);

  try {
    await api.post(
      `/admin/seguros/${target.seguroId}/pilotos/${target.pilotoId}/reactivar`
    );

    setActionMessage({
      type: "success",
      text: "Piloto dado de alta con éxito",
    });

    if (detailSeguro?.id === target.seguroId) {
      const res = await api.get<SeguroDetail>(
        `/admin/seguros/${target.seguroId}`
      );
      setDetailSeguro(res.data);
    }

    void fetchList();
    void fetchPilotos();
  } catch (err: unknown) {
    setActionMessage({
      type: "error",
      text:
        (err as { response?: { data?: { error?: string } } })?.response?.data
          ?.error ?? "No se pudo volver a dar de alta al piloto.",
    });
  } finally {
    setConfirmReactivar(null);
    setActionLoading(null);
  }
};
  const emptyPilotoForm = () => ({
    nif: "",
    nombre: "",
    apellidos: "",
    fecha_nacimiento: "",
    email: "",
    telefono: "",
    direccion: "",
    poblacion: "",
    provincia: "",
    codigo_postal: "",
  });

  const handleAddPilotoAdmin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!detailSeguro?.id) return;
    const modalidadesPaquete = detailSeguro.modalidades_paquete ?? [];
    const mid =
      selectedModalidadId !== ""
        ? selectedModalidadId
        : modalidadesPaquete.length === 1
          ? modalidadesPaquete[0]!.modalidadId
          : null;
    if (mid == null) {
      setPilotoFormError("Selecciona la modalidad contratada");
      return;
    }
    const modalidadElegida = modalidadesPaquete.find((m) => m.modalidadId === mid);
    const esDiario = modalidadElegida?.tipo === "diario";
    let fechaInicio: string | undefined;
    if (fechaCobertura) {
      const dt = new Date(`${fechaCobertura}T${horaCobertura}:${minutosCobertura}:00`);
      if (dt < new Date()) {
        setPilotoFormError("La fecha de inicio debe ser igual o posterior a la fecha actual");
        return;
      }
      fechaInicio = dt.toISOString();
    } else if (esDiario) {
      setPilotoFormError("La fecha de inicio es obligatoria para seguros diarios");
      return;
    }
    setPilotoFormError("");
    setActionLoading("addPiloto");
    try {
      await api.post(`/cliente/seguros/${detailSeguro.id}/pilotos`, {
        ...pilotoForm,
        modalidad_id: mid,
        ...(fechaInicio ? { fecha_inicio: fechaInicio } : {}),
        direccion: pilotoForm.direccion || undefined,
        poblacion: pilotoForm.poblacion || undefined,
        provincia: pilotoForm.provincia || undefined,
        codigo_postal: pilotoForm.codigo_postal || undefined,
      });
      setAddPilotoOpen(false);
      setSelectedModalidadId("");
      setPilotoForm(emptyPilotoForm());
      setFechaCobertura("");
      setHoraCobertura("09");
      setMinutosCobertura("00");
      const res = await api.get<SeguroDetail>(`/admin/seguros/${detailSeguro.id}`);
      setDetailSeguro(res.data);
      fetchList();
    } finally {
      setActionLoading(null);
    }
  };

  const handleGenerarRecibo = async () => {
    if (!detailSeguro?.id) return;
    setActionLoading("recibo");
    try {
      await api.post(`/admin/seguros/${detailSeguro.id}/recibo/regenerar`);
      await loadRecibo(detailSeguro.id);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDescargarCertificadoAdmin = async (
    certificadoUrl: string,
    nombre: string,
    apellidos: string
  ) => {
    try {
      let path: string;
      if (certificadoUrl.startsWith("http")) {
        const url = new URL(certificadoUrl);
        const match = url.pathname.match(/\/api\/assets\/(.+)/);
        if (!match) return;
        path = match[1];
      } else {
        path = certificadoUrl.split("/").map(encodeURIComponent).join("/");
      }

      const response = await api.get(`/assets/${path}`, { responseType: "blob" });
      const blob = new Blob([response.data as BlobPart], { type: "application/pdf" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `certificado-${nombre}-${apellidos}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status !== 401) {
        console.error("Error descargando certificado:", err);
      }
    }
  };

  const columns = [
    { key: "id", header: "ID", render: (r: SeguroRow) => r.id },
    { key: "cliente", header: "Cliente", render: (r: SeguroRow) => (r.cliente as { razonSocial?: string })?.razonSocial ?? "-" },
    { key: "modalidad", header: "Modalidad", render: (r: SeguroRow) => (r.modalidad as { nombre?: string })?.nombre ?? "-" },
    { key: "evento", header: "Paquete", render: (r: SeguroRow) => (r.eventoReferencia as string)?.trim() || "Sin paquete" },
    { key: "created_at", header: "Creado", render: (r: SeguroRow) => formatFecha(r.createdAt as string | undefined) },
    { key: "pilotos", header: "Pilotos", render: (r: SeguroRow) => r.numPilotos ?? 0 },
    { key: "importe", header: "Importe", render: (r: SeguroRow) => `${Number((r as SeguroRow).importeTotal ?? 0).toFixed(2)} €` },
    { key: "estado", header: "Estado", render: (r: SeguroRow) => estadoBadgeSeguro(r.estado) },
    { key: "actions", header: "Acciones", render: (r: SeguroRow) => <Button size="sm" variant="ghost" onClick={() => openDetail(r.id)}>Ver detalle</Button> },
  ];

  const pilotosColumns = [
    { key: "nif", header: "NIF", render: (r: PilotoListRow) => r.piloto.nif },
    {
      key: "nombre",
      header: "Nombre completo",
      render: (r: PilotoListRow) => `${r.piloto.nombre} ${r.piloto.apellidos}`.trim(),
    },
    { key: "email", header: "Email", render: (r: PilotoListRow) => r.piloto.email },
    { key: "cliente", header: "Cliente", render: (r: PilotoListRow) => r.cliente.razonSocial ?? "-" },
    { key: "modalidad", header: "Modalidad", render: (r: PilotoListRow) => r.modalidad.nombre },
    {
      key: "tipo",
      header: "Tipo",
      render: (r: PilotoListRow) =>
        r.modalidad.tipo === "anual" ? "Anual" : r.modalidad.tipo === "diario" ? "Diario" : "—",
    },
    {
      key: "fecha_inicio",
      header: "Inicio cobertura",
      render: (r: PilotoListRow) => formatFechaHora(r.piloto.fechaInicio),
    },
    {
      key: "fecha_fin",
      header: "Fin cobertura",
      render: (r: PilotoListRow) => formatFechaHora(r.piloto.fechaFin),
    },
    { key: "estado", header: "Estado", render: (r: PilotoListRow) => estadoBadgePiloto(r.piloto.estado) },
    {
      key: "emitido",
      header: "Certificado generado",
      render: (r: PilotoListRow) =>
        r.piloto.emitido ? (
          <span className="text-sm">{formatFecha(r.piloto.emitidoAt ?? undefined)}</span>
        ) : (
          <Badge variant="warning">Pendiente</Badge>
        ),
    },
    {
      key: "facturable",
      header: "Incluir en facturación",
      render: (r: PilotoListRow) =>
        r.piloto.esFacturable && !r.piloto.bajaAt && r.piloto.estado === "pagado" ? (
          <Badge variant="success">Sí</Badge>
        ) : (
          <Badge variant="danger">No</Badge>
        ),
    },
    {
      key: "certificado",
      header: "Certificado",
      render: (r: PilotoListRow) =>
        r.piloto.certificadoUrl ? <Badge variant="success">Sí</Badge> : <Badge variant="neutral">No</Badge>,
    },
  ];

  const pilotosTotalPages = Math.max(1, Math.ceil(pilotosTotal / pilotosLimit));

  const resetPagesOnFilter = () => {
    setPage(1);
    setPilotosPage(1);
  };

  const modalidadesPaqueteAdmin = detailSeguro?.modalidades_paquete ?? [];
  const modalidadElegidaAdmin = modalidadesPaqueteAdmin.find(
    (m) =>
      m.modalidadId ===
      (selectedModalidadId !== ""
        ? selectedModalidadId
        : modalidadesPaqueteAdmin.length === 1
          ? modalidadesPaqueteAdmin[0]!.modalidadId
          : -1)
  );
  const esDiarioAddAdmin = modalidadElegidaAdmin?.tipo === "diario";
  const esAnualAddAdmin = modalidadElegidaAdmin?.tipo === "anual";
  const showModalidadSelectAdmin = modalidadesPaqueteAdmin.length > 1;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-[#2C2C2C]">Seguros</h1>
      {actionMessage && (
  <div
    role={actionMessage.type === "error" ? "alert" : "status"}
    className={`fixed right-4 top-4 z-[100] max-w-md rounded-lg border px-4 py-3 text-sm shadow-lg ${
      actionMessage.type === "success"
        ? "border-green-200 bg-green-50 text-green-800"
        : "border-red-200 bg-red-50 text-red-800"
    }`}
  >
    {actionMessage.text}
  </div>
)}

      {listError && (
        <p className="rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {listError}
        </p>
      )}

      <div className="space-y-3 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
          <ClienteSearchSelect
            label="Cliente"
            value={filters.cliente_id}
            onChange={(value) => {
              setFilters((f) => ({ ...f, cliente_id: value }));
              resetPagesOnFilter();
            }}
          />
          <Select
            label="Estado"
            options={ESTADO_OPTIONS}
            value={filters.estado}
            onChange={(e) => {
              setFilters((f) => ({ ...f, estado: e.target.value }));
              resetPagesOnFilter();
            }}
            className="w-full"
          />
          <Select
            label="Modalidad"
            options={[
              { value: "", label: "Todas" },
              ...modalidades.map((m) => ({ value: String(m.id), label: m.nombre })),
            ]}
            value={filters.modalidad_id}
            onChange={(e) => {
              setFilters((f) => ({ ...f, modalidad_id: e.target.value }));
              resetPagesOnFilter();
            }}
            className="w-full"
          />
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <Select
            label="Filtrar fechas por"
            options={[
              { value: "cobertura", label: "Inicio de cobertura" },
              { value: "facturacion", label: "Validación / pago" },
            ]}
            value={filters.fecha_referencia}
            onChange={(e) => {
              setFilters((f) => ({ ...f, fecha_referencia: e.target.value }));
              resetPagesOnFilter();
            }}
            className="w-52"
          />
          <Input
            type="date"
            label="Desde"
            value={filters.fecha_desde}
            onChange={(e) => {
              setFilters((f) => ({ ...f, fecha_desde: e.target.value }));
              resetPagesOnFilter();
            }}
            className="w-36"
          />
          <Input
            type="date"
            label="Hasta"
            value={filters.fecha_hasta}
            onChange={(e) => {
              setFilters((f) => ({ ...f, fecha_hasta: e.target.value }));
              resetPagesOnFilter();
            }}
            className="w-36"
          />
          <div className="flex gap-2 mt-auto">
            <Button variant="secondary" onClick={handleLimpiarFiltros}>
              Limpiar filtros
            </Button>
            <Button variant="secondary" onClick={handleExportarPilotos} loading={exportPilotosLoading}>
              Exportar pilotos Excel
            </Button>
          </div>
        </div>
        {exportPilotosError && <p className="text-sm text-red-600">{exportPilotosError}</p>}
      </div>

      <div className="flex border-b mb-4">
        <button
          type="button"
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            viewMode === "seguros"
              ? "border-[#1A3AB8] text-[#1A3AB8]"
              : "border-transparent text-gray-500 hover:text-gray-700"
          }`}
          onClick={() => setViewMode("seguros")}
        >
          Seguros
        </button>
        <button
          type="button"
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            viewMode === "pilotos"
              ? "border-[#1A3AB8] text-[#1A3AB8]"
              : "border-transparent text-gray-500 hover:text-gray-700"
          }`}
          onClick={() => setViewMode("pilotos")}
        >
          Pilotos
        </button>
      </div>

      {viewMode === "pilotos" && (
        <div className="flex gap-3 mb-4">
          <Input
            label="NIF"
            placeholder="Buscar por NIF..."
            value={filtroPilotoNif}
            onChange={(e) => {
              setFiltroPilotoNif(e.target.value);
              setPilotosPage(1);
            }}
            className="w-40"
          />
          <Input
            label="Nombre"
            placeholder="Nombre o apellidos..."
            value={filtroPilotoNombre}
            onChange={(e) => {
              setFiltroPilotoNombre(e.target.value);
              setPilotosPage(1);
            }}
            className="w-48"
          />
        </div>
      )}

      {viewMode === "seguros" ? (
        <>
          <Table<SeguroRow>
            columns={columns}
            data={data}
            loading={loading}
            emptyMessage="No hay seguros"
            floatingHorizontalScrollbar
          />
          <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
        </>
      ) : (
        <>
          <Table<PilotoListRow>
            columns={pilotosColumns}
            data={pilotosList}
            loading={pilotosLoading}
            emptyMessage="No hay pilotos con estos filtros"
            floatingHorizontalScrollbar
          />
          <Pagination page={pilotosPage} totalPages={pilotosTotalPages} onPageChange={setPilotosPage} />
        </>
      )}

      <Modal isOpen={modalOpen} onClose={() => { setModalOpen(false); setDetailSeguro(null); setReciboData(null); setAddPilotoOpen(false); }} title="Detalle del seguro" footer={null} size="xl">
        {!detailSeguro ? (
          <div className="py-8 text-center text-gray-500">Cargando…</div>
        ) : (
          <>
            <div className="mb-4 flex gap-2 border-b border-gray-200">
              {(["resumen", "pilotos", "recibo"] as const).map((tab) => (
                <button key={tab} type="button" onClick={() => { setDetailTab(tab); if (tab === "recibo") loadRecibo(detailSeguro.id); }} className={`border-b-2 px-4 py-2 text-sm font-medium ${detailTab === tab ? "border-[#1A3AB8] text-[#1A3AB8]" : "border-transparent text-gray-600"}`}>
                  {tab === "resumen" ? "Resumen" : tab === "pilotos" ? "Pilotos" : "Recibo"}
                </button>
              ))}
            </div>

            {detailTab === "resumen" && (
              <div className="space-y-4">
                <dl className="grid gap-2 text-sm">
                  <dt className="font-medium text-gray-500">Cliente</dt><dd>{detailSeguro.cliente?.razonSocial ?? "-"}</dd>
                  <dt className="font-medium text-gray-500">Modalidad</dt>
                  <dd>
                    {modalidadesPaqueteAdmin.length > 0
                      ? modalidadesPaqueteAdmin
                          .map((m) => `${m.nombre} (${m.tipo === "anual" ? "A" : "D"})`)
                          .join(", ")
                      : "-"}
                  </dd>
                  <dt className="font-medium text-gray-500">Paquete</dt><dd>{detailSeguro.eventoReferencia?.trim() || "Sin paquete"}</dd>
                  <dt className="font-medium text-gray-500">Vigencia cobertura</dt><dd>{vigenciaPilotosFacturables(detailSeguro.pilotos)}</dd>
                  <dt className="font-medium text-gray-500">Método pago</dt><dd>{detailSeguro.metodoPago ?? "-"}</dd>
                  <dt className="font-medium text-gray-500">Estado</dt><dd>{estadoBadgeSeguro(detailSeguro.estado)}</dd>
                  <dt className="font-medium text-gray-500">Importes</dt><dd>Prima: {Number(detailSeguro.importePrima ?? 0).toFixed(2)} € · Gastos: {Number(detailSeguro.gastosGestion ?? 0).toFixed(2)} € · Total: {Number(detailSeguro.importeTotal ?? 0).toFixed(2)} €</dd>
                </dl>
                <div className="flex flex-wrap gap-2">
                  {detailSeguro.estado === "activo" &&
                    (detailSeguro.pilotos ?? []).some((p) => p.esFacturable && p.estado === "pendiente") && (
                    <Button loading={actionLoading === "estado"} onClick={handleActivarPilotosPendientes}>
                      Activar pilotos pendientes
                    </Button>
                  )}
                  {(detailSeguro.pilotos ?? []).some(
                    (p) => p.estado === "pagado" && !p.emitido
                  ) && (
                    <Button
                      loading={actionLoading === "emitir-todos"}
                      variant="secondary"
                      onClick={() => void handleEmitirTodos()}
                    >
                      Generar certificados pendientes
                    </Button>
                  )}
                  {(detailSeguro.pilotos ?? []).some((p) => p.estado === "pagado") && (
                    <Button variant="secondary" onClick={() => { setDetailTab("recibo"); loadRecibo(detailSeguro.id); }}>Ver recibo</Button>
                  )}
                  <Button variant="secondary" onClick={() => { setConfirmAvisar(detailSeguro.id); }}>Avisar aseguradora</Button>
                  <Button variant="secondary" onClick={() => { setEditForm({ evento_referencia: (detailSeguro.eventoReferencia as string) ?? "" }); setEditModalOpen(true); }}>Editar</Button>
                </div>
              </div>
            )}

            {detailTab === "pilotos" && (
              <div className="space-y-4">
                <div className="flex flex-wrap gap-2">
                  {detailSeguro.estado === "activo" && (
                    <Button
                      size="sm"
                      onClick={() => {
                        const mods = detailSeguro.modalidades_paquete ?? [];
                        setSelectedModalidadId(mods.length === 1 ? mods[0]!.modalidadId : "");
                        setAddPilotoOpen(true);
                        setPilotoForm(emptyPilotoForm());
                      }}
                    >
                      Añadir piloto
                    </Button>
                  )}
                </div>
                <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead><tr className="border-b"><th className="text-left py-2">NIF</th><th className="text-left py-2">Nombre</th><th className="text-left py-2">Email</th><th className="text-left py-2">Teléfono</th><th className="text-left py-2">Inicio cobertura</th><th className="text-left py-2">Fin cobertura</th><th className="text-left py-2">Estado</th><th className="text-left py-2">Certificado generado</th><th className="text-left py-2">Incluir en facturación</th><th className="text-left py-2">Baja</th><th className="text-left py-2">Acciones</th></tr></thead>
                  <tbody>
                    {(detailSeguro.pilotos ?? []).map((p) => (
                      <tr key={p.id} className="border-b">
                        <td className="py-2">{p.nif}</td>
                        <td className="py-2">{p.nombre} {p.apellidos}</td>
                        <td className="py-2">{p.email}</td>
                        <td className="py-2">{p.telefono}</td>
                        <td className="py-2">{formatFechaHora(p.fechaInicio)}</td>
                        <td className="py-2">{formatFechaHora(p.fechaFin)}</td>
                        <td className="py-2">{estadoBadgePiloto(p.estado)}</td>
                        <td className="py-2">
                          {p.emitido ? (
                            <span className="text-sm text-gray-700">{formatFecha(p.emitidoAt ?? undefined)}</span>
                          ) : (
                            <Badge variant="warning">Pendiente</Badge>
                          )}
                        </td>
                        <td className="py-2">{p.esFacturable && !p.bajaAt && p.estado === "pagado" ? <Badge variant="success">Sí</Badge> : <Badge variant="danger">No</Badge>}</td>
                        <td className="py-2">{formatFecha(p.bajaAt)}</td>
                        <td className="py-2">
                          {p.esFacturable && p.estado === "pagado" && !p.emitido && (
                            <Button
                              size="sm"
                              variant="secondary"
                              className="px-2 py-1 text-xs"
                              loading={actionLoading === `emitir-${p.id}`}
                              onClick={() => void handleEmitirPiloto(p.id)}
                            >
                              Generar certificado
                            </Button>
                          )}
                          {p.esFacturable && (
                            <Button
                              size="sm"
                              variant="secondary"
                              className="text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300 px-2 py-1 text-xs"
                              title="Dar de baja forzada a este piloto"
                              onClick={() => setConfirmBaja({ seguroId: detailSeguro.id, pilotoId: p.id })}
                            >
                              Baja forzada
                            </Button>
                          )}
                          
                          {!p.esFacturable && !!p.bajaAt && (
  <Button
    size="sm"
    variant="primary"
    className="px-2 py-1 text-xs"
    loading={actionLoading === `reactivar-${p.id}`}
    title="Volver a dar de alta conservando fechas y certificado"
    onClick={() =>
      setConfirmReactivar({
        seguroId: detailSeguro.id,
        pilotoId: p.id,
      })
    }
  >
    Volver a dar de alta
  </Button>
)}
{p.emitido && p.certificadoUrl && (
                            <button
                              onClick={() => handleDescargarCertificadoAdmin(p.certificadoUrl!, p.nombre, p.apellidos)}
                              className="ml-2 text-[#1A3AB8] hover:underline text-sm font-medium"
                              title="Descargar certificado"
                            >
                              Certificado
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              </div>
            )}

            {detailTab === "recibo" && (
              <div className="space-y-4">
                {reciboData ? (
                  <>
                    <p>Número: {(reciboData.recibo as { numeroRecibo?: string })?.numeroRecibo ?? "-"}</p>
                    <p>PDF: <a href={`/api/admin/seguros/${detailSeguro.id}/recibo/descargar`} target="_blank" rel="noreferrer" className="text-[#1A3AB8] hover:underline">Descargar</a></p>
                  </>
                ) : (
                  <p className="text-gray-500">No hay recibo individual para este seguro.</p>
                )}
                {!reciboData && (
                  <Button loading={actionLoading === "recibo"} onClick={handleGenerarRecibo}>
                    Generar recibo
                  </Button>
                )}
              </div>
            )}
          </>
        )}
      </Modal>

      <Modal isOpen={addPilotoOpen} onClose={() => setAddPilotoOpen(false)} title="Añadir piloto" footer={null}>
        <form onSubmit={handleAddPilotoAdmin} className="space-y-4">
          {showModalidadSelectAdmin && (
            <Select
              label="Modalidad contratada"
              value={selectedModalidadId === "" ? "" : String(selectedModalidadId)}
              onChange={(e) => setSelectedModalidadId(e.target.value ? Number(e.target.value) : "")}
              options={[
                { value: "", label: "Selecciona modalidad" },
                ...modalidadesPaqueteAdmin.map((m) => ({
                  value: String(m.modalidadId),
                  label: `${m.nombre} (${m.tipo === "anual" ? "Anual" : "Diario"})`,
                })),
              ]}
              required
            />
          )}
          <Input label="NIF/NIE/Pasaporte" value={pilotoForm.nif} onChange={(e) => setPilotoForm((f) => ({ ...f, nif: e.target.value }))} required />
          <Input label="Nombre" value={pilotoForm.nombre} onChange={(e) => setPilotoForm((f) => ({ ...f, nombre: e.target.value }))} required />
          <Input label="Apellidos" value={pilotoForm.apellidos} onChange={(e) => setPilotoForm((f) => ({ ...f, apellidos: e.target.value }))} required />
          <Input label="Fecha nacimiento (DD/MM/AAAA)" value={pilotoForm.fecha_nacimiento} onChange={(e) => setPilotoForm((f) => ({ ...f, fecha_nacimiento: e.target.value }))} required />
          <Input label="Email" type="email" value={pilotoForm.email} onChange={(e) => setPilotoForm((f) => ({ ...f, email: e.target.value }))} required />
          <Input label="Teléfono" value={pilotoForm.telefono} onChange={(e) => setPilotoForm((f) => ({ ...f, telefono: e.target.value }))} required />
          <div className="space-y-2">
            <p className="text-sm font-medium text-[#2C2C2C]">
              Fecha inicio cobertura
              {esDiarioAddAdmin ? " *" : " (opcional)"}
            </p>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              <div className="col-span-2 sm:col-span-1">
                <Input
                  label="Fecha"
                  type="date"
                  min={new Date().toISOString().split("T")[0]}
                  value={fechaCobertura}
                  onChange={(e) => setFechaCobertura(e.target.value)}
                  required={esDiarioAddAdmin}
                />
              </div>
              <div>
                <Select
                  label="Hora"
                  value={horaCobertura}
                  onChange={(e) => setHoraCobertura(e.target.value)}
                  options={Array.from({ length: 24 }, (_, i) => ({
                    value: String(i).padStart(2, "0"),
                    label: `${String(i).padStart(2, "0")}:00`,
                  }))}
                />
              </div>
              <div>
                <Select
                  label="Minutos"
                  value={minutosCobertura}
                  onChange={(e) => setMinutosCobertura(e.target.value)}
                  options={[
                    { value: "00", label: "00" },
                    { value: "15", label: "15" },
                    { value: "30", label: "30" },
                    { value: "45", label: "45" },
                  ]}
                />
              </div>
            </div>
            {esAnualAddAdmin && (
              <p className="text-xs text-gray-500">Si no introduces fecha se usará la fecha actual</p>
            )}
          </div>
          {pilotoFormError && <p className="text-red-600 text-sm">{pilotoFormError}</p>}
          {esAnualAddAdmin && (
            <>
              <Input label="Dirección" value={pilotoForm.direccion} onChange={(e) => setPilotoForm((f) => ({ ...f, direccion: e.target.value }))} required />
              <Input label="Población" value={pilotoForm.poblacion} onChange={(e) => setPilotoForm((f) => ({ ...f, poblacion: e.target.value }))} required />
              <Select
                label="Provincia"
                value={pilotoForm.provincia}
                onChange={(e) => setPilotoForm((f) => ({ ...f, provincia: e.target.value }))}
                options={[{ value: "", label: "Selecciona provincia" }, ...PROVINCIAS_ESPANA_OPTIONS]}
                required
              />
              <Input label="Código postal" value={pilotoForm.codigo_postal} onChange={(e) => setPilotoForm((f) => ({ ...f, codigo_postal: e.target.value }))} required />
            </>
          )}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setAddPilotoOpen(false)}>
              Cancelar
            </Button>
            <Button type="submit" loading={actionLoading === "addPiloto"}>
              Añadir
            </Button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={editModalOpen} onClose={() => setEditModalOpen(false)} title="Editar seguro" footer={
        <>
          <Button variant="secondary" onClick={() => setEditModalOpen(false)}>Cancelar</Button>
          <Button loading={actionLoading === "edit"} onClick={handleSaveEdit}>Guardar</Button>
        </>
      }>
        <div className="space-y-4">
          <Input label="Paquete" value={editForm.evento_referencia} onChange={(e) => setEditForm((f) => ({ ...f, evento_referencia: e.target.value }))} />
        </div>
      </Modal>

      <ConfirmDialog isOpen={!!confirmAvisar} onClose={() => setConfirmAvisar(null)} onConfirm={handleAvisarAseguradora} title="Avisar aseguradora" message="¿Enviar solicitud de emisión a la aseguradora?" />
      <ConfirmDialog isOpen={!!confirmBaja} onClose={() => setConfirmBaja(null)} onConfirm={handleBajaForzada} title="Dar de baja forzada" message="¿Dar de baja a este piloto? Se enviará notificación." confirmLabel="Dar de baja" variant="danger" />
        <ConfirmDialog
  isOpen={!!confirmReactivar}
  onClose={() => setConfirmReactivar(null)}
  onConfirm={handleReactivarPiloto}
  title="Volver a dar de alta"
  message="Se conservarán las fechas de cobertura, el certificado y todos los datos del piloto. El piloto volverá a incluirse en facturación. ¿Continuar?"
  confirmLabel="Volver a dar de alta"
/>
    </div>
  );
}
